// Utility functions for finding and interacting with DOM elements

import { POSITIONING } from './tooltipConstants';

/** Tutorial full flow: only target this patient's card for ready_to_record and review-note steps */
const TUTORIAL_MOCK_PATIENT_DESCRIPTION = "QQQVAINTERFACE,IFC";

/**
 * Finds the .note card that contains the tutorial mock patient name (e.g. "QQQVAINTERFACE,IFC").
 * Returns the LAST match: new test patients are appended to the bottom of the
 * schedule, so with duplicates from repeated tutorial runs the bottom-most one
 * is the patient the current run generated.
 * @returns {Element|null}
 */
function getTutorialMockPatientCard() {
  const cards = document.querySelectorAll(".note");
  let lastMatch = null;
  for (const card of cards) {
    const nameEl = card.querySelector("h2");
    if (nameEl && nameEl.textContent?.trim() === TUTORIAL_MOCK_PATIENT_DESCRIPTION) {
      lastMatch = card;
    }
  }
  return lastMatch;
}

function getFirstTutorialPatientCardByStep(stepId) {
  const cards = document.querySelectorAll(".note");
  for (const card of cards) {
    if (stepId === "ready_to_record") {
      if (card.querySelector('a[data-testid="record-link"]')) {
        return card;
      }
      continue;
    }

    if (stepId === "review_note" || stepId === "locked_note") {
      const reviewButton = card.querySelector('button[id="review_note"]');
      if (!reviewButton) {
        continue;
      }
      const text = reviewButton.textContent?.trim() || "";
      if (stepId === "review_note" && !text.includes("Locked") && !text.includes("View Only")) {
        return card;
      }
      if (stepId === "locked_note" && text.includes("Locked")) {
        return card;
      }
    }
  }
  return null;
}

/**
 * Finds an element with retry logic, useful for elements inside modals
 * @param {string} selector - CSS selector for the element
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} delay - Delay between retries in milliseconds
 * @returns {Promise<Element|null>} - The found element or null
 */
export const findElementWithRetry = (
  selector,
  maxRetries = 10,
  delay = 200,
  stepId = null,
  options = {}
) => {
  const isFullTutorial = options?.isFullTutorial !== false;
  return new Promise((resolve) => {
    let attempts = 0;

    const tryFind = () => {
      // For review_note / locked_note, anchor to the tutorial patient's Review
      // button. Its id (#review_note) is only rendered on the tutorial anchor
      // patient, so resolve it directly and IMMEDIATELY — do not wait for the
      // button label to flip to "Locked": the note is locked right away on the
      // backend and the label only updates after the patient list refetches.
      if ((stepId === 'locked_note' || stepId === 'review_note') && selector === '#review_note') {
        const anchorButton = document.querySelector('button#review_note');
        if (anchorButton) {
          resolve(anchorButton);
          return;
        }
        // Fallback: scan the tutorial patient's card for its Review button.
        const card = isFullTutorial
          ? getTutorialMockPatientCard()
          : getFirstTutorialPatientCardByStep(stepId);
        const fallbackButton = card ? card.querySelector('button[id="review_note"]') : null;
        if (fallbackButton) {
          resolve(fallbackButton);
          return;
        }
      }
      // For manage_dot_phrase, resolve the "My Dot Phrases" tab by its label
      // text inside the stable wrapper — the library-rendered tab markup
      // (roles/nesting) is not guaranteed, so nth-of-type selectors are brittle.
      else if (stepId === 'manage_dot_phrase') {
        const wrapper = document.querySelector('#manage_dot_phrase_button');
        if (wrapper) {
          const tabButton = Array.from(wrapper.querySelectorAll('button')).find(
            (btn) => (btn.textContent || '').trim() === 'My Dot Phrases'
          );
          if (tabButton) {
            resolve(tabButton);
            return;
          }
        }
        const bySelector = document.querySelector(selector);
        if (bySelector) {
          resolve(bySelector);
          return;
        }
      }
      // For ready_to_record, use mock patient in full tutorial, otherwise first matching ready card.
      else if (stepId === 'ready_to_record' && selector === '#ready_to_record') {
        const card = isFullTutorial
          ? getTutorialMockPatientCard()
          : getFirstTutorialPatientCardByStep('ready_to_record');
        const found = card ? card.querySelector(selector) : null;
        if (found) {
          resolve(found);
          return;
        }
      } else {
        // For other steps, use normal logic
        const found = document.querySelector(selector);

        if (found) {
          const modal = found.closest('.modal');
          if (modal) {
            if (modal.classList.contains('show')) {
              resolve(found);
              return;
            } else if (attempts < maxRetries) {
              attempts++;
              setTimeout(tryFind, delay);
              return;
            }
          } else {
            resolve(found);
            return;
          }
        }
      }

      // Element not found yet
      if (attempts < maxRetries) {
        attempts++;
        setTimeout(tryFind, delay);
      } else {
        resolve(null);
      }
    };

    tryFind();
  });
};

/**
 * Checks if an element is visible in the viewport
 * @param {Element} element - The element to check
 * @returns {boolean} - True if element is visible
 */
export const isElementVisible = (element) => {
  if (!element) return false;
  
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 && 
    rect.bottom <= window.innerHeight && 
    rect.left >= 0 && 
    rect.right <= window.innerWidth
  );
};

/**
 * Checks if there's a patient with "Ready to Record" status
 * @returns {boolean} - True if ready to record element exists
 */
export const hasReadyToRecordPatient = () => {
  const readyToRecordElement = document.querySelector('#ready_to_record');
  return !!readyToRecordElement;
};

/**
 * Performs auto-action on an element (click or input)
 * @param {string|object} action - Action configuration
 * @param {string} targetSelector - CSS selector for the target element
 * @returns {void}
 */
export const performAutoAction = (action, targetSelector) => {
  // Handle action - can be string or object
  const actionType = typeof action === 'string' ? action : action?.type;
  
  if (!actionType) {
    return;
  }
  
  // Use action.targetSelector if provided, otherwise use main targetSelector
  const selectorToUse = (typeof action === 'object' && action?.targetSelector) 
    ? action.targetSelector 
    : targetSelector;
  
  if (!selectorToUse) {
    return;
  }
  
  // Safety check: For settings-related selectors, verify we're on the settings page
  const settingsSelectors = ['#add_new_dot_phrase', '#add_new_dot_phrase_button', '#manage_dot_phrase', '#manage_dot_phrase_button', '#upload_macros_panel'];
  const isSettingsSelector = settingsSelectors.some(s => selectorToUse.includes(s));
  if (isSettingsSelector) {
    const currentPath = window.location.pathname;
    if (currentPath !== '/settings') {
      // Don't perform action if we're not on the settings page
      return;
    }
  }
  
  // For leave_feedback_button, use retry logic since it might not be immediately available
  if (selectorToUse === '#leave_feedback_button') {
    let attempts = 0;
    const maxRetries = 15;
    const delay = 100;
    
    const tryClick = () => {
      const element = document.querySelector(selectorToUse);
      if (element) {
        // Click the element
        element.click();
        // Also trigger a change event to ensure React state updates
        const changeEvent = new Event('change', { bubbles: true });
        element.dispatchEvent(changeEvent);
        return;
      }
      if (attempts < maxRetries) {
        attempts++;
        setTimeout(tryClick, delay);
      }
    };
    tryClick();
    return;
  }
  
  // For record-link, use retry logic since it might not be immediately available
  if (selectorToUse.includes('record-link')) {
    let attempts = 0;
    const maxRetries = 20;
    const delay = 100;
    
    const tryClick = () => {
      // First try the exact selector
      let element = document.querySelector(selectorToUse);
      
      // If not found, try alternative selectors (including scoped to #ready_to_record)
      if (!element || element.tagName !== 'A') {
        const altSelectors = [
          'a[data-testid="record-link"]',
          '#ready_to_record a[data-testid="record-link"]',
          '#ready_to_record a'
        ];
        for (const altSelector of altSelectors) {
          const altElement = document.querySelector(altSelector);
          if (altElement && altElement.tagName === 'A') {
            element = altElement;
            break;
          }
        }
      }
      
      if (element && element.tagName === 'A') {
        // Create a synthetic click event
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        element.dispatchEvent(clickEvent);
        // Also try the native click method
        element.click();
        return;
      } else if (element) {
        // If we found an element but it's not an <a>, try to find the <a> inside it
        const linkElement = element.querySelector('a[data-testid="record-link"]') || element.closest('a[data-testid="record-link"]');
        if (linkElement) {
          linkElement.click();
          return;
        }
      } else {
        // Try to find any record-link element
        const allRecordLinks = document.querySelectorAll('[data-testid="record-link"]');
        for (const link of allRecordLinks) {
          if (link.tagName === 'A') {
            link.click();
            return;
          }
        }
      }
      
      if (attempts < maxRetries) {
        attempts++;
        setTimeout(tryClick, delay);
      }
    };
    tryClick();
    return;
  }
  
  // For manage_dot_phrase_button, use retry logic since tab change might take time
  if (selectorToUse === '#manage_dot_phrase_button') {
    // Safety check: only perform if on settings page
    const currentPath = window.location.pathname;
    if (currentPath !== '/settings') {
      return;
    }
    
    let attempts = 0;
    const maxRetries = 10;
    const delay = 100;
    
    const tryClick = () => {
      const element = document.querySelector(selectorToUse);
      if (element) {
        element.click();
        return;
      }
      if (attempts < maxRetries) {
        attempts++;
        setTimeout(tryClick, delay);
      }
    };
    tryClick();
    return;
  }
  
  const element = document.querySelector(selectorToUse);
  if (!element) {
    return;
  }
  
  if (actionType === 'click') {
    // Auto-click the element
    element.click();
  } else if (actionType === 'input') {
    // Auto-focus the input
    element.focus();
    // Optionally set a value if specified in config
    if (action?.value) {
      element.value = action.value;
      // Trigger input event so React state updates
      const event = new Event('input', { bubbles: true });
      element.dispatchEvent(event);
    }
  }
};
